import {
  type ID,
  type NodeItem,
  selectBlockParent,
  selectBlocksByNode,
  selectNodeChildren,
  selectNodeParent,
  selectRootNodesBySection,
  selectSectionsByTab,
  selectTabNodes,
} from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type { AppState } from '@store/types'
import type { PublishNodeItem } from '@storeOld/reducers/publish'
import { useSelector } from 'react-redux'

import { DEFAULT_SECTION_NAME } from '@/constants/defaultNames'
import { buildMenuTree, flattenArray } from '../helper'

type NodeItemWithUniqIndex = NodeItem & { uniqIndex?: number }

export const usePublishData = (activeTabId: string) => {
  const projectName = useSelector(migrationProjectSelector.getProjectTitle)
  const avatar = useSelector(migrationProjectSelector.getProjectAvatar)
  const tree = useSelector<AppState, NodeItem[]>((state) =>
    selectTabNodes(activeTabId)({
      project: state.manager.project,
      relations: state.relations,
    })
  )

  const projectFrame = useSelector(migrationProjectSelector.getProjectFrame)

  const treeArr = useSelector<AppState, NodeItemWithUniqIndex[]>((state) => {
    return tree.map((t, index) => ({
      ...t,
      name: t.title || 'Page',
      uniqIndex: index,
      color: migrationProjectSelector.getProjectColorHexOrDefault(t.colorId)(
        state
      ),
    }))
  })

  const blocks = useSelector<AppState, any>((state) => {
    // Get all block IDs for nodes in treeArr
    const blockIds = treeArr.reduce((acc, node) => {
      const nodeBlockIds = selectBlocksByNode(node.id)(state)
      return [...acc, ...nodeBlockIds]
    }, [] as string[])

    // Create byId and byNodeId structure matching example.json
    return blockIds.reduce(
      (acc, id) => {
        const block =
          migrationProjectSelector.getProjectBlockOrSymbolData(id)(state)

        if (!block) {
          return acc
        }

        const nodeId = selectBlockParent(id)(state)[0]

        const colorHex = migrationProjectSelector.getProjectColorHexOrDefault(
          block.colorId
        )(state)

        const blockImageData =
          block.visual.type === 'image' ? block.visual.image : null

        const blockWireframeData =
          block.visual.type === 'wireframes' &&
          block.visual.wireframes.length > 0
            ? block.visual.wireframes.join(',')
            : ''

        // Add block to byId as an object keyed by ID
        const isSymbol = block.type === 'symbol'
        const symbolId = isSymbol ? block.symbolId : null

        const blockData = {
          id: block.id,
          body: block.content || null,
          name: block.title || '',
          user: '',
          color: colorHex,
          image: blockImageData,
          nodeId,
          isSymbol,
          symbolId,
          completed: block.completed || false,
          wireframe: blockWireframeData,
          childIndex: 0,
          subscribers: [],
        }

        // Create/update byNodeId entry
        const byNodeId = { ...acc.byNodeId }
        if (!byNodeId[nodeId]) {
          byNodeId[nodeId] = []
        }
        byNodeId[nodeId].push(block.id)

        return {
          byId: {
            ...acc.byId,
            [block.id]: blockData,
          },
          byNodeId,
        }
      },
      {
        byId: {},
        byNodeId: {} as Record<string, any>,
      }
    )
  })

  const sectionIds = useSelector(selectSectionsByTab(activeTabId))
  const sortedSectionIds = useSelector(
    migrationProjectSelector.getProjectSectionIdsSortedByIds(sectionIds)
  )

  const groupedBySections = useSelector<AppState, any>((state) => {
    return sortedSectionIds.reduce((acc, sectionId) => {
      const sectionItem =
        migrationProjectSelector.getProjectSectionById(sectionId)(state)

      const rootNodeIds: ID[] = selectRootNodesBySection(sectionId)(state)

      const getNodeWithChildren = (id: string): NodeItemWithUniqIndex[] => {
        const node = migrationProjectSelector.getProjectNodeById(id)(state)
        if (!node) {
          return []
        }

        const childIds = selectNodeChildren(id)(state) || []

        const childNodes = childIds.flatMap((childId) =>
          getNodeWithChildren(childId)
        )

        const parentId = selectNodeParent(id)(state)?.[0] || null
        const uniqIndex = treeArr.find((item) => item.id === id)?.uniqIndex

        const nodeWithExtras = {
          ...node,
          uniqIndex,
          parentId,
        }

        return [nodeWithExtras, ...childNodes]
      }

      const treeBySections = rootNodeIds.flatMap((id) =>
        getNodeWithChildren(id)
      )

      return [
        ...acc,
        {
          name: sectionItem?.title || DEFAULT_SECTION_NAME,
          order: sectionItem?.order || 0,
          tree: treeBySections,
        },
      ]
    }, [] as any)
  })

  const menu = groupedBySections.map(
    (
      section: {
        name: string
        order: number
        tree: {
          id: string
          title: string
          parentId: string | null
          uniqIndex?: number | undefined
        }[]
      },
      index: number
    ) => ({
      name: section.name,
      order: section.order,
      items: buildMenuTree(section.tree, index),
    })
  )

  const slugs = flattenArray(menu)

  const treeArrWithSlugs = treeArr.map((item) => ({
    ...item,
    slug: slugs[item.id],
  })) as PublishNodeItem[]

  return {
    projectName,
    avatar,
    blocks,
    tree: treeArrWithSlugs,
    menu,
    projectFrame,
  }
}
